import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  DisablableOption,
  ModelBase,
  ReusableString,
} from "@models";

import API from "@dndUtilities/system_api";

import StringBox from "@components/input/strings/StringBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ModelBaseInput from "@dndComponents/ModelBaseInput";
import CommonUtilities from "@utilities/common_utilities";
import ButtonBox from "@components/input/buttons/ButtonBox";
import ToastBox from "@components/display/ToastBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import SelectStringBox from '@components/input/selects/SelectStringBox';

interface Props { }

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ReusableStringBox({
}: Props) {
  const [selected_reusable_string, setSelectedReusableString] = useState<ReusableString | null>(null);
  const [options, setOptions] = useState<DisablableOption[]>([]);
  const [adding_new, setAddingNew] = useState<ReusableString | null>(null);
  const [viewing, setViewing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [change_time, setChangeTime] = useState(-1);
  const [processing, setProcessing] = useState(false);
  const [toast_time, setToastTime] = useState(-1);

  useEffect(() => {
    const load = () => {
      const strings = api.all_reusable_strings;
      setOptions(strings.map(o => {
        const opt: DisablableOption = {
          id: o._id,
          text: o.name
        };
        return opt;
      }));
    }
    load();
  }, []);

  const submit = async () => {
    if (api.user) {
      if (adding_new &&
        (api.user.admin || 
          adding_new._id === "" || 
          adding_new.owner_id === api.user._id)) {
        setProcessing(true);
        if (!api.user.admin || adding_new.source_books_old.filter(o => o.id === "Personal").length > 0) {
          adding_new.owner_id = api.user._id;
        } else if (api.user.admin && adding_new.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
          adding_new.owner_id = "";
        }
        const res = await api.upsertObject("reusable_string", adding_new);
        setProcessing(false);
        setAddingNew(null);
        const new_reusable_string = res ? api.all_reusable_strings.find(o => o._id === res.body.id) : undefined;
        if (new_reusable_string) {
          setOptions(options => [...options,
            {
              id: new_reusable_string._id, 
              text: new_reusable_string.name
            }]);
          setSelectedReusableString(new_reusable_string);
        }
      } else if (editing && selected_reusable_string) {
        await api.upsertObject("reusable_string", selected_reusable_string);
        setProcessing(false);
        setEditing(false);
        const new_reusable_string = api.all_reusable_strings.find(o => o._id === selected_reusable_string._id);
        if (new_reusable_string) {
          setSelectedReusableString(new_reusable_string);
        }
      }
    }
  }

  const renderAddingNew = () => {
    if (adding_new) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="header"
            size={{
              xs: 12
            }}>New Reusable String</Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ModelBaseInput
              obj={adding_new}
              disabled={false}
              change_time={change_time}
              onSubmit={submit}
              onCancel={() => { setAddingNew(null); }}
              processing={processing}
              onChange={(changed: ModelBase) => {
                if (changed instanceof ReusableString) {
                  const new_obj = adding_new;
                  new_obj.copy(changed);
                  setAddingNew(new_obj);
                  setChangeTime(common_util.get_time_number());
                }
              }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <StringBox
                    name="Label"
                    value={adding_new.label}
                    onBlur={(changed: string) => {
                      const new_obj = adding_new;
                      new_obj.label = changed;
                      setAddingNew(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
              </Grid>
            </ModelBaseInput>
          </Grid>
        </Grid>
      );
    }
    return <></>;
  }

  const renderEditing = () => {
    if (editing && selected_reusable_string) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="header"
            size={{
              xs: 12
            }}>New Reusable String</Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <ModelBaseInput
              obj={selected_reusable_string}
              disabled={false}
              change_time={change_time}
              onSubmit={submit}
              onCancel={() => { setEditing(false); }}
              processing={processing}
              onChange={(changed: ModelBase) => {
                if (changed instanceof ReusableString) {
                  const new_obj = selected_reusable_string;
                  new_obj.copy(changed);
                  setSelectedReusableString(new_obj);
                  setChangeTime(common_util.get_time_number());
                }
              }}>
              <Grid container spacing={0} direction="row">
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <StringBox
                    name="Label"
                    value={selected_reusable_string.label}
                    onBlur={(changed: string) => {
                      const new_obj = selected_reusable_string;
                      new_obj.label = changed;
                      setSelectedReusableString(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid>
                {/* <Grid item xs={12}>
                  <StringBoxWithReusables
                    name="Content"
                    value={selected_reusable_string.content}
                    onChange={(changed: string) => {
                      const new_obj = selected_reusable_string;
                      new_obj.content = changed;
                      setSelectedReusableString(new_obj);
                      setChangeTime(common_util.get_time_number());
                    }}
                  />
                </Grid> */}
              </Grid>
            </ModelBaseInput>
          </Grid>
        </Grid>
      );
    }
    return <></>;
  }

  const renderViewing = () => {
    if (viewing && selected_reusable_string) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            className="header"
            size={{
              xs: 12
            }}>
            { selected_reusable_string.label }
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements
              display_string={selected_reusable_string.content}
            />
          </Grid>
        </Grid>
      );
    }
    return <></>;
  }

  // debugger;
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Reusable String"
          options={options}
          extra_options={ api.user && api.user.admin && api.online ? ["Add New..."] : [] }
          value={selected_reusable_string?._id??""}
          onChange={(changed: string) => {
            if (changed === "Add New...") {
              const new_adding_new = new ReusableString();
              debugger; // Make sure it does working_on_sb correctly
              if (api.user && api.user.admin) {
                api.applyWorkingOn(new_adding_new);
              }
              setAddingNew(new_adding_new);
              setSelectedReusableString(null);
            } else {
              const new_reusable_string = api.all_reusable_strings.find(o => o._id === changed);
              if (new_reusable_string) {
                setSelectedReusableString(new_reusable_string);
              }
            }
          }}
        />
      </Grid>
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 8
        }}>
        <Grid
          size={{
            xs: 4
          }}>
          <ButtonBox
            name="View"
            disabled={selected_reusable_string === null}
            onClick={() => {
              setViewing(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <ButtonBox
            name="Edit"
            disabled={selected_reusable_string === null || !api.online}
            onClick={() => {
              setEditing(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <ButtonBox
            name="Copy Link"
            disabled={selected_reusable_string === null}
            onClick={() => {
              if (selected_reusable_string) {
                navigator.clipboard.writeText(common_util.genLink(selected_reusable_string, "Replacer")); // `[Replacer:reusable_string:${selected_reusable_string?._id??""}]`);
                setToastTime(common_util.get_time_number());
              }
            }}
          />
        </Grid>
      </Grid>
      <ModalOrDrawer
        open={adding_new !== null}
        onClose={() => {
          setAddingNew(null);
        }}
      >
        { renderAddingNew() }
      </ModalOrDrawer>
      <ModalOrDrawer
        open={editing}
        onClose={() => {
          setEditing(false);
        }}
      >
        { renderEditing() }
      </ModalOrDrawer>
      <ModalOrDrawer
        open={viewing}
        onClose={() => {
          setViewing(false);
        }}
      >
        { renderViewing() }
      </ModalOrDrawer>
      <ToastBox
        openTime={ toast_time }
      >
        Link copied to Clipboard
      </ToastBox>
    </Grid>
  );
}
